import { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

/**
 * Universal rename modal — matches the EventRenameModal style so every
 * "pencil" across Studio, Library, Galleries, Reels, Directory, etc.
 * feels identical.
 *
 * Props:
 *   label       – title shown above the current value ("Rename Video", …)
 *   currentName – the value being renamed (also seeded into the input)
 *   subtitle    – small explanation shown below the current name (optional)
 *   testIdPrefix – e.g. "video-rename" → gives us
 *                  `video-rename-modal|input|save|cancel|close` testids
 *   onSave(newName) – async fn; return anything (or throw) to signal success/failure
 *   onClose         – called after a successful save AND when user cancels
 *   maxLen          – hard cap on input length (default 200)
 */
export default function RenameModal({
  label,
  currentName,
  subtitle,
  testIdPrefix = "rename",
  onSave,
  onClose,
  maxLen = 200,
}) {
  const [value, setValue] = useState(currentName || "");
  const [saving, setSaving] = useState(false);

  const trimmed = (value || "").trim();
  const isDirty = trimmed && trimmed !== (currentName || "").trim();

  const save = async () => {
    if (!isDirty) { onClose(); return; }
    setSaving(true);
    try {
      await onSave(trimmed);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Rename failed";
      toast.error(`Rename failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`${testIdPrefix}-modal`}
    >
      <div
        className="bg-stone-950 border border-orange-700 max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="min-w-0">
            <div className="label-tiny mb-1">{label}</div>
            <h2 className="font-display uppercase text-2xl font-bold leading-tight break-words">
              {currentName || <span className="text-stone-500">(unnamed)</span>}
            </h2>
            {subtitle && (
              <p className="text-xs text-stone-500 mt-1">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-200 shrink-0"
            data-testid={`${testIdPrefix}-close`}
          >
            ✕
          </button>
        </div>
        <div className="mb-4">
          <div className="label-tiny mb-1">New name</div>
          <input
            autoFocus
            value={value}
            maxLength={maxLen}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") onClose();
            }}
            className="field-input w-full"
            data-testid={`${testIdPrefix}-input`}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
            data-testid={`${testIdPrefix}-cancel`}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!isDirty || saving}
            className="btn-primary inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`${testIdPrefix}-save`}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={12} />}
            Rename
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tiny helper button — an outlined pencil that opens the given onClick.
 * Keeps every "rename" pencil visually identical across the app.
 */
export function RenamePencilButton({ onClick, title = "Rename", testId, size = 12, className = "" }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick && onClick(e); }}
      className={`p-1.5 border border-stone-700 hover:border-orange-500 text-stone-400 hover:text-orange-400 transition-colors inline-flex items-center justify-center ${className}`}
      title={title}
      data-testid={testId}
    >
      <Pencil size={size} />
    </button>
  );
}
